import { useState, useRef, useCallback } from "react";
import { Shield, Upload, Loader2, Download, FileCheck, X, RotateCcw, AlertTriangle, Eye } from "lucide-react";
import { Button } from "../components/ui/button";
import AdUnit from "../components/AdUnit";
import axios from "axios";

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api`;
const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.tiff', '.pdf', '.docx', '.xlsx', '.pptx'];

export default function MetadataCleaner() {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [inspecting, setInspecting] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [metadata, setMetadata] = useState(null);
  const [cleaned, setCleaned] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const validateFile = (f) => {
    const ext = '.' + f.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setError(`Unsupported format. Use: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return false;
    }
    return true;
  };

  const handleFile = useCallback(async (f) => {
    setError("");
    setCleaned(false);
    setMetadata(null);
    if (!validateFile(f)) return;
    setFile(f);

    // Auto-inspect
    setInspecting(true);
    try {
      const formData = new FormData();
      formData.append("file", f);
      const res = await axios.post(`${API}/metadata/inspect`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMetadata(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not read file metadata.");
    } finally {
      setInspecting(false);
    }
  }, []);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  }, [handleFile]);

  const [downloadUrl, setDownloadUrl] = useState(null);

  const handleClean = async () => {
    if (!file) return;
    setCleaning(true);
    setError("");
    setDownloadUrl(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/metadata/clean`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        responseType: "blob",
      });

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      setDownloadUrl(url);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `clean_${file.name}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setCleaned(true);
    } catch (err) {
      setError("Failed to clean file. Please try again.");
    } finally {
      setCleaning(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setMetadata(null);
    setCleaned(false);
    setError("");
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const metadataCount = metadata?.metadata ? Object.keys(metadata.metadata).length : 0;
  const hasSensitiveData = metadata?.metadata && (
    metadata.metadata.GPSInfo || metadata.metadata.GPSLatitude || metadata.metadata.GPSLongitude ||
    metadata.metadata.author || metadata.metadata.creator || metadata.metadata.last_modified_by ||
    metadata.metadata.Author || metadata.metadata.Creator
  );

  return (
    <div className="min-h-[calc(100vh-4rem)]">
      {/* Header */}
      <section className="py-12 md:py-16 bg-[#F8FAFC] border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#FFFBEB] flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#F59E0B]" />
            </div>
            <span className="label-mono">Metadata Cleaner</span>
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900"
            style={{ fontFamily: 'Chivo, sans-serif' }}
            data-testid="metadata-page-title"
          >
            Clean file metadata
          </h1>
          <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl">
            Remove hidden data like GPS location, author name, and edit history from images, PDFs, and documents before sharing.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left: Upload & Actions */}
          <div className="lg:col-span-5 space-y-6">
            {/* Drop zone */}
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${
                dragActive
                  ? "drop-zone-active border-[#F59E0B] bg-amber-50/50"
                  : "border-slate-300 hover:border-slate-400 bg-slate-50"
              }`}
              data-testid="metadata-drop-zone"
            >
              <input
                ref={inputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.bmp,.webp,.tiff,.pdf,.docx,.xlsx,.pptx"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                data-testid="metadata-file-input"
              />
              <Upload className="w-8 h-8 text-slate-400 mx-auto mb-4" />
              <p className="text-sm font-medium text-slate-700 mb-1">
                Drop your file here
              </p>
              <p className="text-xs text-slate-400">
                Images, PDF, DOCX, XLSX, PPTX
              </p>
            </div>

            {/* File info */}
            {file && (
              <div className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg bg-white" data-testid="metadata-file-info">
                <Shield className="w-8 h-8 text-[#F59E0B] flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{file.name}</p>
                  <p className="text-xs text-slate-400">{formatSize(file.size)}</p>
                </div>
                <button onClick={handleReset} className="text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={handleClean}
                disabled={cleaning || !metadata}
                className="bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-md px-6 h-11 font-medium btn-press"
                data-testid="clean-button"
              >
                {cleaning ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Cleaning...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-2" />
                    Clean & Download
                  </>
                )}
              </Button>
              {file && (
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="rounded-md h-11 px-4 border-slate-300 text-slate-600 btn-press"
                  data-testid="metadata-reset-button"
                >
                  <RotateCcw className="w-4 h-4 mr-1.5" />
                  Reset
                </Button>
              )}
            </div>

            {error && <p className="text-sm text-red-500" data-testid="metadata-error">{error}</p>}

            {cleaned && (
              <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg" data-testid="clean-success">
                <FileCheck className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-800">File cleaned successfully</p>
                  {downloadUrl && (
                    <a href={downloadUrl} download={`clean_${file?.name || 'file'}`} target="_blank" rel="noopener noreferrer" className="text-sm text-[#F59E0B] hover:underline font-medium" data-testid="metadata-manual-download">
                      Click here if download didn't start
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Ad */}
            <AdUnit format="vertical" />
          </div>

          {/* Right: Metadata Display */}
          <div className="lg:col-span-7">
            {inspecting && (
              <div className="border border-slate-200 rounded-lg bg-white min-h-[400px] flex items-center justify-center">
                <div className="flex items-center gap-3 text-slate-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-sm">Scanning file metadata...</span>
                </div>
              </div>
            )}

            {!inspecting && metadata && (
              <div className="border border-slate-200 rounded-lg bg-white">
                <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-slate-500" />
                    <span className="label-mono">Found {metadataCount} metadata fields</span>
                  </div>
                  {hasSensitiveData && (
                    <div className="flex items-center gap-1.5 text-xs text-amber-600 font-medium">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Sensitive data detected
                    </div>
                  )}
                </div>
                <div className="p-5 max-h-[500px] overflow-y-auto" data-testid="metadata-list">
                  {metadataCount === 0 ? (
                    <p className="text-sm text-slate-400">No metadata found in this file.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {Object.entries(metadata.metadata).map(([key, value]) => {
                        const isSensitive = ['GPSInfo', 'GPSLatitude', 'GPSLongitude', 'author', 'creator', 'last_modified_by', 'Author', 'Creator', 'lastModifiedBy'].includes(key);
                        return (
                          <div
                            key={key}
                            className={`flex items-start gap-3 py-2 px-3 rounded-md text-sm ${
                              isSensitive ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'
                            }`}
                            data-testid={`metadata-field-${key}`}
                          >
                            <span className="font-mono text-xs font-medium text-slate-600 min-w-[140px] flex-shrink-0 pt-0.5">
                              {key}
                              {isSensitive && <AlertTriangle className="w-3 h-3 text-amber-500 inline ml-1.5" />}
                            </span>
                            <span className="text-slate-500 break-all text-xs">
                              {String(value).length > 200 ? String(value).slice(0, 200) + '...' : String(value)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {!inspecting && !metadata && (
              <div className="border border-slate-200 rounded-lg bg-white min-h-[400px] flex items-center justify-center">
                <div className="text-center p-8">
                  <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-sm text-slate-400">Upload a file to scan for hidden metadata</p>
                  <p className="text-xs text-slate-300 mt-1">Supports JPEG, PNG, PDF, DOCX, XLSX, PPTX</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
